import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal } from '../../Smart/Modal/Modal';
import { I18nReactContext } from '../../../contexts/I18nContext';
import { ImportUsersPreview } from './ImportUsersPreview';
import './ImportUsersPreviewModal.scss';

/**
 * Modal wrapper for ImportUsersPreview component.
 * Opens as a separate wider modal to display the import preview tables.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Array} props.matchedUsers - Users matched from the import file
 * @param {Array} props.unmatchedRows - Rows that didn't match any user
 * @param {Function} props.onConfirm - Callback when user confirms adding matched users
 * @param {boolean} [props.isLoading=false] - Whether the component is in loading state
 * @returns {JSX.Element} The ImportUsersPreviewModal component
 */
export const ImportUsersPreviewModal = ({
    open,
    onClose,
    matchedUsers,
    unmatchedRows,
    onConfirm,
    isLoading = false
}) => {
    const { i18n } = useContext(I18nReactContext);

    if (!open) return null;

    /**
     * Handles confirm and closes the modal
     */
    const handleConfirm = (userIds) => {
        onConfirm(userIds);
        onClose();
    };

    /**
     * Handles cancel and closes the modal
     */
    const handleCancel = () => {
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose}>
            <div className="ImportUsersPreviewModal modalWrapper">
                <div className="modalTitle">
                    <span>{i18n.translate('signDocModal.importUsers.modalTitle')}</span>
                </div>

                <div className="modalContent">
                    <ImportUsersPreview
                        matchedUsers={matchedUsers}
                        unmatchedRows={unmatchedRows}
                        onConfirm={handleConfirm}
                        onCancel={handleCancel}
                        isLoading={isLoading}
                    />
                </div>
            </div>
        </Modal>
    );
};

ImportUsersPreviewModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    matchedUsers: PropTypes.array,
    unmatchedRows: PropTypes.array,
    onConfirm: PropTypes.func.isRequired,
    isLoading: PropTypes.bool
};

export default ImportUsersPreviewModal;
